import { useState } from 'react';
import { trackButtonClick } from '../../../utils/stats';

interface ProblemCard {
  icon: string;
  title: string;
  details: string[];
  accentColor: string;
  gradientFrom: string;
  gradientTo: string;
}

export default function Problems() {
  const [activeTab, setActiveTab] = useState<'without' | 'with'>('without');

  const handleTabClick = (tab: 'without' | 'with') => {
    setActiveTab(tab);
    trackButtonClick(tab === 'without' ? 'int_p_nai' : 'int_p_ai', tab === 'without' ? '痛点-没有AI' : '痛点-有AI');
  };

  const problemsWithoutAI: ProblemCard[] = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: '成本高，效率低',
      details: [
        '培训、管理成本居高不下',
        '高峰扛不住，低峰浪费资源',
        "服务无法全天候"
      ],
      accentColor: '#0066ff',
      gradientFrom: 'from-[#0066ff]/8',
      gradientTo: 'to-[#00a3ff]/5',
    },
    {
      icon: 'ri-user-unfollow-line',
      title: '质量不稳，难标准化',
      details: [
        '新手上手慢，老员工负担重',
        '回答质量参差，投诉难改善',
        '培训与质检长期高投入'
      ],
      accentColor: '#7c5cfc',
      gradientFrom: 'from-[#7c5cfc]/8',
      gradientTo: 'to-[#a78bfa]/5',
    },
    {
      icon: 'ri-emotion-sad-line',
      title: '压力大、流失高',
      details: [
        '负面情绪多，心理消耗大',
        '服务经验难沉淀',
        'KPI严苛，效率与质量难以兼顾'
      ],
      accentColor: '#0066ff',
      gradientFrom: 'from-[#0066ff]/8',
      gradientTo: 'to-[#7c5cfc]/5',
    },
    {
      icon: 'ri-fire-line',
      title: '数据散，问题杂',
      details: [
        '客户问题反复且杂，对话分散',
        '缺乏系统分析和复盘',
        '管理层只能看到结果，看不到原因'
      ],
      accentColor: '#7c5cfc',
      gradientFrom: 'from-[#7c5cfc]/8',
      gradientTo: 'to-[#0066ff]/5',
    },
  ];

  const problemsWithAI: ProblemCard[] = [
    {
      icon: 'ri-robot-line',
      title: 'AI能答，时效差',
      details: [
        'AI 参与度低，存在感弱',
        '实际效果与预期差距大',
        'AI只能回答少部分问题'
      ],
      accentColor: '#0066ff',
      gradientFrom: 'from-[#0066ff]/8',
      gradientTo: 'to-[#00a3ff]/5',
    },
    {
      icon: 'ri-arrow-left-right-line',
      title: '转人工繁，难优化',
      details: [
        '反复追问仍转人工',
        '优化凭经验，无数据支撑',
        '缺乏系统化归因分析'
      ],
      accentColor: '#7c5cfc',
      gradientFrom: 'from-[#7c5cfc]/8',
      gradientTo: 'to-[#a78bfa]/5',
    },
    {
      icon: 'ri-book-open-line',
      title: '知识有限，更新慢',
      details: [
        '知识分散在多系统文档',
        '更新依赖人工，易滞后',
        '业务变化快，AI容易过时'
      ],
      accentColor: '#0066ff',
      gradientFrom: 'from-[#0066ff]/8',
      gradientTo: 'to-[#7c5cfc]/5',
    },
    {
      icon: 'ri-tools-line',
      title: '系统固化，难迭代',
      details: [
        '工具对接难，交付即结束',
        '效果不好，不知道如何优化',
        '效果难以评估'
      ],
      accentColor: '#7c5cfc',
      gradientFrom: 'from-[#7c5cfc]/8',
      gradientTo: 'to-[#0066ff]/5',
    },
  ];

  const currentProblems = activeTab === 'without' ? problemsWithoutAI : problemsWithAI;

  return (
    <section className="relative py-16 sm:py-20 md:py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-white via-[#f5f8ff] to-[#f0edff]" />
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-100/30 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/3" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-purple-100/40 rounded-full blur-[100px] translate-x-1/3 translate-y-1/4" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-12 md:mb-14">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
            你的客服系统，是否在经历
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0066ff] to-[#7c5cfc]">
              这些问题
            </span>
            ？
          </h2>
        </div>

        <div className="flex justify-center mb-8 sm:mb-10 md:mb-12 px-4">
          <div className="inline-flex items-center bg-white rounded-2xl p-1.5 shadow-sm border border-gray-100 gap-2 w-full sm:w-auto">
            <button
              onClick={() => handleTabClick('without')}
              className={`relative flex-1 sm:flex-none px-4 sm:px-7 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-medium transition-all whitespace-nowrap cursor-pointer overflow-hidden ${
                activeTab === 'without'
                  ? 'text-white shadow-md'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {activeTab === 'without' && (
                <div className="absolute inset-0 bg-gradient-to-r from-[#0066ff] to-[#5b4cff] rounded-xl" />
              )}
              <span className="relative z-10 flex items-center justify-center">
                <i className="ri-close-circle-line mr-1 sm:mr-1.5" />
                没有AI的困境
              </span>
            </button>

            <button
              onClick={() => handleTabClick('with')}
              className={`relative flex-1 sm:flex-none px-4 sm:px-7 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-medium transition-all whitespace-nowrap cursor-pointer overflow-hidden ${
                activeTab === 'with'
                  ? 'text-white shadow-md'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {activeTab === 'with' && (
                <div className="absolute inset-0 bg-gradient-to-r from-[#0066ff] to-[#5b4cff] rounded-xl" />
              )}
              <span className="relative z-10 flex items-center justify-center">
                <i className="ri-error-warning-line mr-1 sm:mr-1.5" />
                有AI但不够好
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6 px-4">
          {currentProblems.map((problem, index) => (
            <div
              key={`${activeTab}-${index}`}
              className="group relative bg-white/80 backdrop-blur-sm rounded-2xl p-5 sm:p-6 md:p-7 border border-white/60 hover:border-transparent hover:shadow-2xl hover:shadow-blue-100/40 transition-all duration-500 hover:-translate-y-2 animate-fadeIn flex flex-col"
              style={{ animationDelay: `${index * 80}ms` }}
            >
              <div
                className="absolute top-0 left-6 right-6 h-[3px] rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: `linear-gradient(90deg, ${problem.accentColor}, ${
                    problem.accentColor === '#0066ff' ? '#7c5cfc' : '#0066ff'
                  })`,
                }}
              />

              <div
                className={`absolute top-0 right-0 w-28 h-28 bg-gradient-to-br ${problem.gradientFrom} ${problem.gradientTo} rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
              />

              <div className="relative flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-4 sm:mb-5">
                  <div
                    className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                    style={{
                      background: `linear-gradient(135deg, ${problem.accentColor}12, ${problem.accentColor}08)`,
                      border: `1px solid ${problem.accentColor}18`,
                    }}
                  >
                    <span className="text-lg sm:text-xl font-bold" style={{ color: problem.accentColor }}>
                      {index + 1}
                    </span>
                  </div>

                  <h4 className="text-sm sm:text-[15px] font-bold text-gray-800 leading-snug group-hover:text-gray-900 transition-colors flex-1">
                    {problem.title}
                  </h4>
                </div>

                <div className="space-y-2 sm:space-y-2.5 mb-4 sm:mb-5 flex-1">
                  {problem.details.map((detail, i) => (
                    <div key={i} className="flex items-start gap-2 sm:gap-2.5">
                      <span
                        className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                        style={{ backgroundColor: `${problem.accentColor}50` }}
                      />
                      <span className="text-xs sm:text-[13px] text-gray-600 leading-relaxed">{detail}</span>
                    </div>
                  ))}
                </div>

                {/* <div
                  className="pt-3 sm:pt-4 border-t border-dashed"
                  style={{ borderColor: `${problem.accentColor}15` }}
                >
                  <div className="flex items-start gap-2 min-h-[40px] sm:min-h-[44px]">
                    <i
                      className="ri-arrow-right-s-fill text-sm mt-0.5 flex-shrink-0"
                      style={{ color: problem.accentColor }}
                    />
                    <p className="text-xs sm:text-[13px] font-semibold text-gray-700 leading-relaxed">
                      {problem.summary}
                    </p>
                  </div>
                </div> */}
              </div>
            </div>
          ))}
        </div>

        {/* 底部提示文案已移除 */}
      </div>
    </section>
  );
}
